import { useSelector } from 'react-redux';

import { selectFromMkadKm, selectHighwayIds } from 'shared/selectors/jsonQuery';
import { FC, useCallback, useMemo } from 'react';
import { selectLocation } from 'shared/selectors/location';
import { useAppDispatch, useJsonQueryBackup } from 'shared/hooks';
import { SPB_AND_REGION } from 'shared/constants/regions';
import { getRegionId } from 'shared/utils/geo';
import { DirectionsModal } from '../../../../../../packages/GeoComponents';
import { SearchButtonContainer } from '../../SearchButtonContainer';
import { selectDirectionsModal } from 'shared/selectors/geo';
import { updateSearchMeta } from 'shared/thunks/searchMeta';
import { setMaxFromMkad, setMinFromMkad } from 'shared/slices/jsonQuery';
import { applyFilters } from 'shared/thunks/filters';
import { updateTags } from 'shared/thunks/tags';
import { removeHighways, selectHighways } from 'shared/thunks/geo/directions';

interface IDirectionsModalContainerProps {
  onModalClose: VoidFunction;
}

export const DirectionsModalContainer: FC<IDirectionsModalContainerProps> = ({ onModalClose }) => {
  const dispatch = useAppDispatch();
  const fromMKAD = useSelector(selectFromMkadKm);
  const selectedHighwayIds = useSelector(selectHighwayIds);
  const { data, isLoading } = useSelector(selectDirectionsModal);
  const currentLocation = useSelector(selectLocation);
  const { resetJsonQuery } = useJsonQueryBackup();

  const label = useMemo(
    () => (SPB_AND_REGION.includes(getRegionId(currentLocation)) ? 'От КАД:' : 'От МКАД:'),
    [currentLocation],
  );

  const handleMinFromMKADChange = useCallback(
    (value: number | null) => {
      dispatch(setMinFromMkad(value));
      dispatch(updateSearchMeta());
    },
    [dispatch],
  );

  const handleMaxFromMKADChange = useCallback(
    (value: number | null) => {
      dispatch(setMaxFromMkad(value));
      dispatch(updateSearchMeta());
    },
    [dispatch],
  );

  const handleHighwaysSelect = useCallback(
    (highwaysIds: number[]) => {
      dispatch(selectHighways({ highwaysIds }));
    },
    [dispatch],
  );

  const handleClose = useCallback(() => {
    onModalClose();
    resetJsonQuery();
  }, [onModalClose, resetJsonQuery]);

  const handleApply = useCallback(() => {
    onModalClose();
    dispatch(applyFilters());
    dispatch(updateTags(true));
  }, [dispatch, onModalClose]);

  const handleHighwaysDeselect = useCallback(
    (highwaysIds: number[]) => {
      dispatch(removeHighways(highwaysIds));
    },
    [dispatch],
  );

  return (
    <DirectionsModal
      fromMKAD={fromMKAD}
      isLoading={!!isLoading}
      directions={data}
      selectedHighwayIds={selectedHighwayIds}
      label={label}
      changeMaxFromMKAD={handleMaxFromMKADChange}
      changeMinFromMKAD={handleMinFromMKADChange}
      close={handleClose}
      selectHighways={handleHighwaysSelect}
      deselectHighways={handleHighwaysDeselect}
      searchButton={<SearchButtonContainer onClick={handleApply} />}
    />
  );
};
